/**
 * Утилиты для извлечения user-friendly сообщений из HttpError.
 *
 * Бэк отвечает по-разному:
 *   • 401 Unauthorized — auth-redirect, тут просто общий текст
 *   • 403 Forbidden — нет прав
 *   • 404 Not Found — объект не найден
 *   • 409 Conflict — обычно конфликт по времени/уникальности
 *   • 422 Validation — body может быть { errors: { field: [msgs] } }
 *     или { errors: [{ field, message }] } или { message }
 *   • 5xx — серверная ошибка
 *
 * extractErrorMessage делает максимум усилий чтобы вытащить осмысленный
 * текст; если не получилось — возвращает дефолт.
 */

import {HttpError} from '@services/utils';

type ValidationErrors =
  Record<string, string | string[]> | Array<{field?: string; message?: string}>;

const STATUS_FALLBACKS: Record<number, string> = {
  401: 'Сессия истекла — войдите снова',
  403: 'Нет прав на это действие',
  404: 'Не найдено',
  408: 'Слишком долгий запрос — повторите',
  409: 'Конфликт — возможно такая запись уже есть',
  413: 'Файл слишком большой',
  429: 'Слишком много запросов — подождите немного',
  500: 'Ошибка сервера — попробуйте позже',
  502: 'Бэкенд недоступен — попробуйте позже',
  503: 'Сервис временно недоступен',
  504: 'Бэкенд не успел ответить — попробуйте позже',
};

/**
 * Распарсить validation errors из body 422-ответа в человекочитаемую
 * сводку. Берём первое сообщение каждого поля, склеиваем через ' · '.
 */
const flattenValidationErrors = (errors: ValidationErrors): string | null => {
  if (Array.isArray(errors)) {
    const msgs = errors.map((e) => e?.message).filter((m): m is string => Boolean(m));
    return msgs.length > 0 ? msgs.join(' · ') : null;
  }
  if (errors && typeof errors === 'object') {
    const msgs: string[] = [];
    for (const [field, val] of Object.entries(errors)) {
      const first = Array.isArray(val) ? val[0] : val;
      if (first) msgs.push(`${field}: ${first}`);
    }
    return msgs.length > 0 ? msgs.join(' · ') : null;
  }
  return null;
};

/**
 * Главная функция — превратить неизвестную ошибку в строку для toast'а
 * или Result-плашки.
 */
export const extractErrorMessage = (err: unknown, fallback = 'Что-то пошло не так'): string => {
  if (!err) return fallback;

  if (err instanceof HttpError) {
    const body = err.body as
      | {
          message?: string;
          error?: string;
          errors?: ValidationErrors;
          payload?: {message?: string; errors?: ValidationErrors};
        }
      | null
      | undefined;

    // 422 — пробуем достать validation errors
    if (err.status === 422) {
      const errs = body?.errors ?? body?.payload?.errors;
      const flat = errs ? flattenValidationErrors(errs) : null;
      if (flat) return flat;
    }

    // BE-message (прямой message или вложенный payload.message)
    const msg = body?.message ?? body?.payload?.message ?? body?.error;
    if (typeof msg === 'string' && msg.trim()) return msg;

    // Fallback по статусу
    const statusFallback = STATUS_FALLBACKS[err.status];
    if (statusFallback) return statusFallback;

    return err.message || fallback;
  }

  if (err instanceof Error && err.message) {
    return err.message;
  }

  return fallback;
};

/** Достать HTTP-статус если ошибка типа HttpError, иначе null. */
export const getHttpStatus = (err: unknown): number | null => {
  return err instanceof HttpError ? err.status : null;
};

/**
 * Тело ответа об ошибке как объект — чтобы читать из него поля,
 * которые бэк кладёт рядом с кодом: `openApplicationId`, `clinicId`,
 * `retryAfter`, `createdAt`.
 */
export const getErrorBody = (err: unknown): Record<string, unknown> | null => {
  if (!(err instanceof HttpError)) return null;
  return typeof err.body === 'object' && err.body !== null
    ? (err.body as Record<string, unknown>)
    : null;
};

/**
 * Машинный код ошибки — поле `error` в теле (`already_client`,
 * `open_lead_exists`, …).
 *
 * Разводить сценарии нужно по нему, а не по тексту `message`: текст
 * бэк вправе переписать в любой момент — он для человека, — и ветка
 * интерфейса, пришитая к формулировке, тихо отвалится.
 */
export const getErrorCode = (err: unknown): string | null => {
  const code = getErrorBody(err)?.error;
  return typeof code === 'string' && code !== '' ? code : null;
};

/** Строковое поле из тела ошибки (ссылочные id приходят строками). */
export const getErrorString = (err: unknown, key: string): string | null => {
  const v = getErrorBody(err)?.[key];
  return typeof v === 'string' && v !== '' ? v : null;
};
